import { resolveThemeToken } from '@/composables/useThemeTokens'

/**
 * The rendered preview runs inside a sandboxed `srcdoc` iframe, so its scrollbars
 * belong to that document and no parent stylesheet — or the `customScrollbar` mixin —
 * can reach them. This injects the DS scrollbar into the preview document instead, so
 * the rendered view matches the code view and the rest of the app.
 *
 * D33: the preview shows the page as published, and visitors get a light page whatever
 * scheme the app is in. So the scrollbar matches that light page, not the app scheme:
 * the colours are resolved once, in a subtree pinned to light (`data-ui-theme="light"`),
 * and written in as concrete values — the iframe document does not load the ui-lib
 * stylesheet. They stay #aaaab6 / #57576b in both app schemes, so nothing needs to be
 * re-injected on a scheme change and nothing script-like is added to the page.
 *
 * Shape mirrors `customScrollbar` in `@foundever/ui-lib/mixins.scss`: 4px track, pill
 * thumb on `--interactive-border-hover`, `--icon-secondary` on hover/active.
 *
 * Preview only — the published page is served from `content` untouched, so real
 * visitors still get whatever scrollbar the page itself defines. This is injected
 * ahead of the page's own styles so a page that deliberately styles its scrollbar
 * still wins.
 */
export interface IPreviewScrollbarColors {
  /** Resting thumb. */
  thumb: string
  /** Thumb while hovered or dragged. */
  thumbActive: string
}

let lightColors: IPreviewScrollbarColors | undefined

/** The scrollbar colours as the light scheme resolves them, read once. */
export function previewScrollbarColors(): IPreviewScrollbarColors {
  if (lightColors) return lightColors
  const el = document.createElement('div')
  el.style.display = 'none'
  el.dataset.uiTheme = 'light'
  const host = document.body ?? document.documentElement
  host.appendChild(el)
  try {
    lightColors = {
      thumb: resolveThemeToken('--interactive-border-hover', el),
      thumbActive: resolveThemeToken('--icon-secondary', el),
    }
  } finally {
    el.remove()
  }
  return lightColors
}

export function previewScrollbarStyleSource(
  { thumb, thumbActive }: IPreviewScrollbarColors = previewScrollbarColors(),
): string {
  return `::-webkit-scrollbar{width:4px;height:4px}
::-webkit-scrollbar:hover{width:4px;height:4px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background-color:${thumb};border-radius:9999px}
::-webkit-scrollbar-thumb:hover,::-webkit-scrollbar-thumb:active{background-color:${thumbActive}}
@supports not selector(::-webkit-scrollbar){html{scrollbar-color:${thumb} transparent;scrollbar-width:thin}}`
}
